import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  CheckIcon,
  CopyIcon,
  MailIcon,
  RotateCcwIcon,
  SendIcon,
} from "lucide-react";
import { ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { SiGmail } from "react-icons/si";

export type EmailData = {
  subject: string;
  body: string;
  to?: string;
};

export type MessageResponseBlockEmailProps = ComponentProps<"div"> & {
  email: EmailData;
};

export function MessageResponseBlockEmail({
  email,
  className,
  ...props
}: MessageResponseBlockEmailProps) {
  const { t } = useTranslation();
  const [copiedText, setCopiedText] = useCopyToClipboard();
  const [editedTo, setEditedTo] = useState(email.to ?? "");
  const [editedSubject, setEditedSubject] = useState(email.subject);
  const [editedBody, setEditedBody] = useState(email.body);
  const [isReady, setIsReady] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isEdited = useMemo(
    () => editedSubject !== email.subject || editedBody !== email.body,
    [editedSubject, editedBody, email.subject, email.body]
  );

  // Wait 50ms before rendering to avoid height issues on page refresh
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const fullEmailContent = useMemo(() => {
    const toLine = editedTo
      ? `${t("components.emailCard.to")}: ${editedTo}\n\n`
      : "";
    const subjectLine = editedSubject
      ? `${t("components.emailCard.subject")}: ${editedSubject}\n\n`
      : "";
    return toLine + subjectLine + editedBody;
  }, [editedTo, editedSubject, editedBody, t]);

  const isCopied = copiedText === fullEmailContent;

  // Sync state with props when email changes (conversation change or reload)
  useEffect(() => {
    setEditedTo(email.to ?? "");
    setEditedSubject(email.subject);
    setEditedBody(email.body);
  }, [email.to, email.subject, email.body]);

  // Auto-resize textarea based on content
  // Use ResizeObserver to detect when textarea gets proper width, then calculate height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !isReady) return;

    const resizeTextarea = () => {
      const offsetWidth = textarea.offsetWidth;

      // Only resize if textarea has proper width (>100px means parent is sized)
      if (offsetWidth > 100) {
        textarea.style.height = "0px";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    // Initial resize attempt
    resizeTextarea();

    // Watch for size changes (when parent gets proper width)
    const resizeObserver = new ResizeObserver(() => {
      resizeTextarea();
    });

    resizeObserver.observe(textarea);

    return () => {
      resizeObserver.disconnect();
    };
  }, [editedBody, isReady]);

  const handleCopy = () => {
    setCopiedText(fullEmailContent);
  };

  const handleSend = (provider: "default" | "gmail" | "outlook") => {
    const subject = encodeURIComponent(editedSubject);
    const body = encodeURIComponent(editedBody);
    const to = encodeURIComponent(email.to ?? "");

    const urls = {
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`,
      outlook: `https://outlook.office.com/mail/0/deeplink/compose?to=${to}&subject=${subject}&body=${body}`,
      default: `mailto:${to}?subject=${subject}&body=${body}`,
    };

    window.open(urls[provider], "_blank");
  };

  const handleReset = () => {
    setEditedSubject(email.subject);
    setEditedBody(email.body);
  };

  if (!isReady) {
    return null;
  }

  return (
    <div
      data-slot="message-response-block-email"
      className={cn(
        "rounded-xl border border-border bg-card overflow-hidden w-full",
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("components.emailCard.title")}
          </span>
          {isEdited && (
            <span className="text-xs font-medium text-orange-500">
              • Modifié
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Reset button - shown only when edited */}
          {isEdited && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8"
                  onClick={handleReset}
                >
                  <RotateCcwIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("components.emailCard.reset")}</TooltipContent>
            </Tooltip>
          )}

          {/* Copy button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8"
                onClick={handleCopy}
              >
                {isCopied ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <CopyIcon className="w-4 h-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isCopied
                ? t("components.emailCard.copied")
                : t("components.emailCard.copy")}
            </TooltipContent>
          </Tooltip>

          {/* Send dropdown */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="w-8 h-8">
                    <SendIcon className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t("components.emailCard.send")}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleSend("gmail")}>
                <SiGmail className="w-4 h-4 mr-2 text-red-500" />
                {t("components.emailCard.providers.gmail")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSend("outlook")}>
                <PiMicrosoftOutlookLogoFill className="w-4 h-4 mr-2 text-blue-500" />
                {t("components.emailCard.providers.outlook")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSend("default")}>
                <MailIcon className="w-4 h-4 mr-2" />
                {t("components.emailCard.providers.default")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Recipient line - Non-editable and optional */}
        {editedTo && (
          <>
            <div className="flex items-center gap-3">
              <span className="text-base font-semibold text-foreground shrink-0">
                {t("components.emailCard.to")}
              </span>
              <span className="flex-1 px-2 py-1 text-base text-foreground">
                {editedTo}
              </span>
            </div>

            {/* Separator */}
            <div className="border-t border-border" />
          </>
        )}

        {/* Subject line - Editable */}
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-foreground shrink-0">
            {t("components.emailCard.subject")}
          </span>
          <input
            type="text"
            value={editedSubject}
            onChange={(e) => setEditedSubject(e.target.value)}
            className="flex-1 px-2 py-1 text-base transition-colors bg-transparent border border-transparent rounded text-foreground hover:bg-muted/50 focus:outline-hidden focus-visible:outline-hidden focus:bg-muted/50"
            placeholder={t("components.emailCard.objectPlaceholder")}
          />
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Body - Editable */}
        <textarea
          ref={textareaRef}
          value={editedBody}
          onChange={(e) => setEditedBody(e.target.value)}
          className="w-full p-2 text-base leading-relaxed transition-colors bg-transparent border-0 rounded resize-none text-foreground hover:bg-muted/50 focus:outline-hidden focus:bg-muted/50"
          placeholder={t("components.emailCard.bodyPlaceholder")}
          rows={1}
        />
      </div>
    </div>
  );
}

export function MessageResponseBlockEmailSkeleton({
  className,
  ...props
}: ComponentProps<"div">) {
  const { t } = useTranslation();

  return (
    <div
      data-slot="message-response-block-email-skeleton"
      className={cn(
        "rounded-xl border border-border bg-card overflow-hidden w-full animate-in fade-in duration-300",
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <span className="text-sm font-medium text-muted-foreground">
          {t("components.emailCard.title")}
        </span>
        <div className="flex items-center gap-1">
          <Skeleton className="w-8 h-8 rounded-md" />
          <Skeleton className="w-8 h-8 rounded-md" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Subject line skeleton */}
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-foreground shrink-0">
            {t("components.emailCard.subject")}
          </span>
          <Skeleton className="flex-1 h-8 rounded" />
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Body skeleton - multiple lines */}
        <div className="p-2 space-y-2">
          <Skeleton className="w-full h-4 rounded" />
          <Skeleton className="w-11/12 h-4 rounded" />
          <Skeleton className="w-4/5 h-4 rounded" />
          <Skeleton className="w-full h-4 rounded" />
          <Skeleton className="w-3/4 h-4 rounded" />
        </div>
      </div>
    </div>
  );
}
